import { useState, useEffect, useCallback } from 'react';
import { App } from 'antd';
import { diaryTeacherAdminApi } from '../services/diary-teacher/diary-teacher.service';
import {
  DiaryRecordItem,
  AttendanceRecordItem,
  DiaryTeacherFilters,
  School,
  Grade,
} from '../services/diary-teacher/diary-teacher.types';

export const useDiaryTeacher = () => {
  // De App.useApp(): el message estático no se renderiza con React 19.
  const { message } = App.useApp();
  const [diaryRecords, setDiaryRecords] = useState<DiaryRecordItem[]>([]);
  const [attendanceRecords, setAttendanceRecords] = useState<AttendanceRecordItem[]>([]);
  const [schools, setSchools] = useState<School[]>([]);
  const [grades, setGrades] = useState<Grade[]>([]);
  const [weeks, setWeeks] = useState<{ id: number; name: string }[]>([]);
  
  const [diaryLoading, setDiaryLoading] = useState(false);
  const [attendanceLoading, setAttendanceLoading] = useState(false);
  const [schoolsLoading, setSchoolsLoading] = useState(false);
  const [gradesLoading, setGradesLoading] = useState(false);
  const [weeksLoading, setWeeksLoading] = useState(false);
  
  const [diaryPagination, setDiaryPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  
  const [attendancePagination, setAttendancePagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const [filters, setFilters] = useState<DiaryTeacherFilters>({
    page: 1,
    limit: 10,
    sortBy: 'date',
    sortDirection: 'desc',
  });

  // Cargar registros del diario
  const loadDiaryRecords = useCallback(async () => {
    try {
      setDiaryLoading(true);
      setDiaryRecords([]); // Limpiar registros anteriores antes de cargar nuevos
      const response = await diaryTeacherAdminApi.getDiaryRecords(filters);
      
      setDiaryRecords(response.data);
      setDiaryPagination({
        current: response.page,
        pageSize: response.limit,
        total: response.total,
      });
    } catch (error) {
      message.error('Error al cargar los registros del diario');
      console.error('Error loading diary records:', error);
    } finally {
      setDiaryLoading(false);
    }
  }, [filters, message]);

  // Cargar registros de asistencia
  const loadAttendanceRecords = useCallback(async () => {
    try {
      setAttendanceLoading(true);
      setAttendanceRecords([]); // Limpiar registros anteriores antes de cargar nuevos
      const response = await diaryTeacherAdminApi.getAttendanceRecords(filters);
      
      setAttendanceRecords(response.data);
      setAttendancePagination({
        current: response.page,
        pageSize: response.limit,
        total: response.total,
      });
    } catch (error) {
      message.error('Error al cargar los registros de asistencia');
      console.error('Error loading attendance records:', error);
    } finally {
      setAttendanceLoading(false);
    }
  }, [filters, message]);

  // Cargar escuelas
  const loadSchools = useCallback(async () => {
    try {
      setSchoolsLoading(true);
      const response = await diaryTeacherAdminApi.getSchools();
      setSchools(response);
    } catch (error) {
      message.error('Error al cargar las escuelas');
      console.error('Error loading schools:', error);
    } finally {
      setSchoolsLoading(false);
    }
  }, [message]);

  // Cargar grados
  const loadGrades = useCallback(async (schoolId?: number) => {
    try {
      setGradesLoading(true);
      const response = await diaryTeacherAdminApi.getGrades(schoolId);
      setGrades(response);
    } catch (error) {
      message.error('Error al cargar los grados');
      console.error('Error loading grades:', error);
    } finally {
      setGradesLoading(false);
    }
  }, [message]);

  // Cargar semanas
  const loadWeeks = useCallback(async () => {
    try {
      setWeeksLoading(true);
      const response = await diaryTeacherAdminApi.getWeeks();
      setWeeks(response);
    } catch (error) {
      message.error('Error al cargar las semanas');
      console.error('Error loading weeks:', error);
    } finally {
      setWeeksLoading(false);
    }
  }, [message]);

  // Actualizar filtros
  const updateFilters = useCallback((newFilters: Partial<DiaryTeacherFilters>) => {
    setFilters(prev => ({
      ...prev,
      ...newFilters,
      // Solo resetear page a 1 si están cambiando otros filtros que no sean page y limit
      page: newFilters.page !== undefined ? newFilters.page : 
            (newFilters.limit !== undefined ? prev.page : 1), // Solo reset si no es cambio de paginación
    }));
  }, []);

  // Limpiar filtros
  const clearFilters = useCallback(() => {
    setFilters({
      page: 1,
      limit: 10,
      sortBy: 'date',
      sortDirection: 'desc',
    });
  }, []);

  // Cargar datos iniciales
  useEffect(() => {
    loadSchools();
    loadWeeks();
  }, [loadSchools, loadWeeks]);

  return {
    // Data
    diaryRecords,
    attendanceRecords,
    schools,
    grades,
    weeks,
    
    // Loading states
    diaryLoading,
    attendanceLoading,
    schoolsLoading,
    gradesLoading,
    weeksLoading,
    
    // Pagination
    diaryPagination,
    attendancePagination,
    
    // Filters
    filters,
    
    // Actions
    loadDiaryRecords,
    loadAttendanceRecords,
    loadGrades,
    loadWeeks,
    updateFilters,
    clearFilters,
  };
};
